<template>
  <main class="flex justify-center font-zona">
    <div
      class="flex max-[800px]:flex-col gap-5 lg:gap-10 w-full min-[1200px]:w-4/5 max-[1199px]:p-5 py-5"
    >
      <!-- NEWS DETAIL -->
      <div
        v-if="!isLoading && news"
        class="w-full h-fit border shadow p-5 lg:p-10 space-y-5"
      >
        <img :src="news.image" :alt="news.id" class="w-full" />
        <h1 class="font-medium text-2xl">
          {{ lang === "mn" ? news.title : news.title_en }}
        </h1>
        <div
          class="flex max-sm:flex-col items-center text-sm font-light text-gray-500"
        >
          <font-awesome-icon icon="fa-solid fa-user" class="mr-2" />
          <span class="mr-8">{{ news.publisher }}</span>
          <font-awesome-icon icon="fa-solid fa-calendar-days" class="mr-2" />
          <span>{{ news.created_at }}</span>
        </div>

        <div v-if="!news">
          <SpinLoading />
        </div>
        <div v-else>
          <!-- олон зурагтай үед -->
          <div v-if="isCarousel">
            <!-- Carousel -->
            <div class="carousel">
              <button @click="prevImage" class="nav prev">‹</button>
              <img :src="images[currentIndex]" alt="" />
              <button @click="nextImage" class="nav next">›</button>

              <div class="dots">
                <span
                  v-for="(img, index) in images"
                  :key="index"
                  :class="['dot', { active: index === currentIndex }]"
                  @click="goToImage(index)"
                ></span>
              </div>
            </div>

            <!-- Carousel-ын доор зөвхөн текст -->
            <div
              class="news-text leading-6 text-pretty"
              v-html="textWithoutImages"
            ></div>
          </div>

          <!-- ганц зураг эсвэл зураггүй бол бүх HTML-ийг шууд үзүүлнэ -->
          <div
            v-else
            class="leading-6 text-pretty"
            v-html="lang === 'mn' ? news.text : news.text_en"
          ></div>
        </div>

        <!-- share -->
        <div class="flex flex-col justify-center items-center p-5 bg-gray-200">
          <h3 class="text-xl mb-2 tracking-wide">
            {{ lang === "mn" ? "Хуваалцах :" : "Share" }}
          </h3>
          <div class="flex gap-1">
            <button
              @click="shareOnFacebook"
              class="flex justify-center items-center h-8 w-8 bg-[#1877F2] rounded text-white text-xl"
            >
              <font-awesome-icon icon="fa-brands fa-facebook" />
            </button>
            <button
              @click="shareOnTwitter"
              class="flex justify-center items-center h-8 w-8 bg-[#1DA1F2] rounded text-white text-xl"
            >
              <font-awesome-icon icon="fa-brands fa-twitter" />
            </button>
            <button
              @click="shareByEmail"
              class="flex justify-center items-center h-8 w-8 bg-gray-500 rounded text-white text-xl"
            >
              <font-awesome-icon icon="fa-solid fa-envelope" />
            </button>
          </div>
        </div>
      </div>

      <div
        v-else-if="!isLoading && !news"
        class="flex justify-center items-center w-full h-[10rem] md:text-2xl bg-gray-100"
      >
        {{ lang === "mn" ? "Мэдээ олдсонгүй" : "No News Found" }}
      </div>

      <div
        v-else-if="isLoading"
        class="flex justify-center items-center w-full h-[50vh]"
      >
        <SpinLoading />
      </div>

      <NewsPageSide />
    </div>
  </main>
</template>

<script>
import { onMounted, ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import clientInstance from "@/lib/clientInstance";
import NewsPageSide from "@/containers/MiningMongolia/NewsPageSide.vue";
import SpinLoading from "@/components/ui/SpinLoading.vue";

export default {
  name: "MMDetailNews",
  components: { NewsPageSide, SpinLoading },
  setup() {
    const store = useStore();
    const lang = computed(() => store.getters.getLanguage);

    const route = useRoute();
    const isLoading = ref(false);
    const news = ref(null);

    const isCarousel = ref(false);
    const images = ref([]);
    const textWithoutImages = ref("");
    const currentIndex = ref(0);

    const fetchNews = async (newsId) => {
      try {
        isLoading.value = true;
        const res = await clientInstance.get(
          `/get/news/id?id=${newsId}&lang=${lang.value}`
        );
        if (res.status === 200 && res.data.news) {
          news.value = res.data.news;
        }
      } catch (err) {
        return err;
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      fetchNews(route.params.id || 1);
    });

    watch([news, lang], ([val, currentLang]) => {
      if (!val) return;

      const content = currentLang === "mn" ? val.text : val.text_en;

      if (content) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, "text/html");
        const imgs = doc.querySelectorAll("img");

        if (imgs.length > 1) {
          images.value = Array.from(imgs).map((img) => img.src);
          imgs.forEach((img) => img.remove());
          textWithoutImages.value = doc.body.innerHTML;
          isCarousel.value = true;
          currentIndex.value = 0; // carousel-д зориулсан индексийг reset хийх
        } else if (imgs.length === 1) {
          images.value = [imgs[0].src];
          imgs.forEach((img) => img.remove());
          textWithoutImages.value = doc.body.innerHTML;
          isCarousel.value = true;
          currentIndex.value = 0;
        } else {
          images.value = [];
          textWithoutImages.value = doc.body.innerHTML;
          isCarousel.value = false;
        }
      }
    });

    const nextImage = () => {
      currentIndex.value = (currentIndex.value + 1) % images.value.length;
    };

    const prevImage = () => {
      currentIndex.value =
        (currentIndex.value - 1 + images.value.length) % images.value.length;
    };
    const goToImage = (index) => {
      currentIndex.value = index;
    };

    watch([() => route.params.id, lang], ([newId]) => {
      fetchNews(newId || 1);
      news.value = null;
    });

    const shareOnFacebook = () => {
      const url = encodeURIComponent(window.location.href);
      const title = encodeURIComponent(news.value.title || "Mining Mongolia");
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${title}`,
        "_blank"
      );
    };

    const shareOnTwitter = () => {
      const url = encodeURIComponent(window.location.href);
      const title = encodeURIComponent(news.value.title || "Mining Mongolia");
      window.open(
        `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
        "_blank"
      );
    };

    const shareByEmail = () => {
      const url = encodeURIComponent(window.location.href);
      const title = encodeURIComponent(news.value.title || "Mining Mongolia");
      window.location.href = `mailto:?subject=${title}&body=${url}`;
    };

    return {
      lang,
      news,
      isLoading,
      shareOnFacebook,
      shareOnTwitter,
      shareByEmail,
      nextImage,
      prevImage,
      isCarousel,
      images,
      currentIndex,
      goToImage,
      textWithoutImages,
    };
  },
};
</script>

<style scoped>
.carousel {
  position: relative;
  margin: auto;
}
.carousel img {
  width: 100%;
  display: block;
  border-radius: 8px;
}
.nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.4);
  color: white;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 28px;
  border-radius: 4px;
}
.prev {
  left: 10px;
}
.next {
  right: 10px;
}
.dots {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}
.dot {
  height: 10px;
  width: 10px;
  margin: 0 4px;
  background-color: #bbb;
  border-radius: 50%;
  display: inline-block;
  cursor: pointer;
  transition: background-color 0.3s;
}
.dot.active {
  background-color: #333;
}
</style>
